import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { lectures, categories, difficultyConfig } from "@/mocks/lectures";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import RegistrationModal from "@/pages/courses/components/RegistrationModal";
import PopularSlider from "./components/PopularSlider";
import PreviewModal from "./components/PreviewModal";
import { motion } from "motion/react";

export default function LecturesPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [selectedLecture, setSelectedLecture] = useState<{ id: string; title: string } | null>(null);
  const [previewLecture, setPreviewLecture] = useState<(typeof lectures)[0] | null>(null);

  const filtered = useMemo(() => {
    let items = [...lectures];
    if (activeCategory === "free") {
      items = items.filter((l) => l.isFree);
    } else if (activeCategory !== "all") {
      items = items.filter((l) => l.category === activeCategory);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (l) =>
          l.title.toLowerCase().includes(q) ||
          l.description.toLowerCase().includes(q) ||
          l.instructor.toLowerCase().includes(q) ||
          l.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return items;
  }, [activeCategory, searchQuery]);

  const handleRegistration = (lecture: typeof lectures[0]) => {
    setSelectedLecture({ id: lecture.id, title: lecture.title });
    setIsRegistrationOpen(true);
  };

  const handlePreview = (lecture: typeof lectures[0], e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setPreviewLecture(lecture);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-play-circle-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">동영상 강의</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-2">
              동영상 강의
            </h1>
            <p className="text-sm text-[#8C8780]">
              초보자도 쉽게 따라할 수 있는 실전 중심의 경매 동영상 강의를 만나보세요
            </p>
          </motion.div>

          {/* Popular Slider */}
          <PopularSlider />

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="relative mb-6 max-w-md"
          >
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#8C8780]"></i>
            <input
              type="text"
              placeholder="강의 제목, 내용, 강사, 태그 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
            />
          </motion.div>

          {/* Category Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="flex items-center gap-2 mb-8 overflow-x-auto pb-2"
          >
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap ${
                  activeCategory === cat.key
                    ? "bg-[#1C1917] text-white font-medium"
                    : "bg-white border border-[#E8E5E0] text-[#5C5650] hover:border-[#C4956A] hover:text-[#C4956A]"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>

          {/* Results Count */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xs text-[#8C8780] mb-5"
          >
            총 <strong className="text-[#1C1917]">{filtered.length}</strong>개의 강의
          </motion.p>

          {/* Lecture Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((lecture, idx) => (
              <motion.div
                key={lecture.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * idx, duration: 0.4, ease: "easeOut" as const }}
              >
                <Link
                  to={`/lectures/${lecture.id}`}
                  className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all flex flex-col h-full"
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <img loading="lazy" decoding="async"
                      src={lecture.thumbnail}
                      alt={lecture.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg">
                        <i className="ri-play-fill text-xl text-[#1C1917] ml-0.5"></i>
                      </div>
                    </div>
                    {/* Duration */}
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-medium bg-black/70 text-white rounded">
                      {lecture.duration}
                    </div>
                    {/* Free Badge */}
                    {lecture.isFree && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-0.5 text-xs font-bold bg-emerald-500 text-white rounded-full">
                          무료
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 flex flex-col flex-1">
                    {/* Difficulty + Category */}
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${difficultyConfig[lecture.difficulty].bg} ${difficultyConfig[lecture.difficulty].text} ${difficultyConfig[lecture.difficulty].border}`}>
                        {lecture.difficulty}
                      </span>
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#C4956A]/10 text-[#C4956A] rounded border border-[#C4956A]/20">
                        {lecture.categoryLabel}
                      </span>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap items-center gap-1 mb-2">
                      {lecture.tags.map((tag) => (
                        <span key={tag} className="text-[10px] text-[#8C8780] hover:text-[#C4956A] transition-colors cursor-default">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <h3 className="text-sm font-semibold text-[#1C1917] mb-2 line-clamp-2 leading-snug group-hover:text-[#C4956A] transition-colors">
                      {lecture.title}
                    </h3>
                    <p className="text-xs text-[#8C8780] mb-3 line-clamp-2 leading-relaxed">
                      {lecture.description}
                    </p>

                    {/* Spacer */}
                    <div className="flex-1"></div>

                    {/* Info */}
                    <div className="flex items-center justify-between text-xs text-[#8C8780] pt-3 border-t border-[#F0EDE8] mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-star-line text-[#C4956A]"></i>
                        {lecture.instructor}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {lecture.views.toLocaleString()}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleRegistration(lecture);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold rounded-lg bg-[#C4956A] text-white hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-check-line"></i>
                        수강 신청하기
                      </button>
                      <button
                        onClick={(e) => handlePreview(lecture, e)}
                        className="flex items-center justify-center gap-1 py-2.5 px-3 text-xs font-medium rounded-lg border border-[#E8E5E0] text-[#5C5650] hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-eye-line"></i>
                        3분 미리보기
                      </button>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Empty State */}
          {filtered.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
                <i className="ri-video-line text-2xl text-[#C4956A]"></i>
              </div>
              <p className="text-[#5C5650] font-medium mb-1">
                검색 결과가 없습니다
              </p>
              <p className="text-sm text-[#8C8780]">
                다른 카테고리나 검색어로 다시 시도해보세요
              </p>
            </motion.div>
          )}

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <div className="bg-[#1C1917] rounded-2xl p-10 md:p-14">
              <h2 className="text-2xl font-bold text-white mb-3">
                아직도 혼자 고민하시나요?
              </h2>
              <p className="text-sm text-white/60 mb-8 max-w-[400px] mx-auto">
                체계적인 커리큘럼으로 설계된 교육과정과 함께라면 누구나 경매
                투자 전문가가 될 수 있습니다
              </p>
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C4956A] text-white text-sm font-semibold rounded-full hover:bg-[#B08050] transition-all"
              >
                <i className="ri-book-open-line"></i>
                교육과정 보러 가기
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {selectedLecture && (
        <RegistrationModal
          isOpen={isRegistrationOpen}
          onClose={() => setIsRegistrationOpen(false)}
          courseTitle={selectedLecture.title}
          courseId={selectedLecture.id}
        />
      )}

      {previewLecture && (
        <PreviewModal
          isOpen={true}
          onClose={() => setPreviewLecture(null)}
          title={previewLecture.title}
          previewContent={previewLecture.previewContent}
          instructor={previewLecture.instructor}
          duration={previewLecture.duration}
          curriculum={previewLecture.curriculum}
        />
      )}
    </div>
  );
}